"use client"

import React, { useState, useCallback, useRef, DragEvent } from 'react'
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { Upload, X, File, Image, FileText, Download } from "lucide-react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const MAX_FILES = 5

interface MultiDocumentUploadProps {
  onFilesChange: (files: File[]) => void
  files: File[] | string[]
  required?: boolean
  className?: string
}

export function MultiDocumentUpload({
  onFilesChange,
  files,
  required = false,
  className = ''
}: MultiDocumentUploadProps) {
  const [dragActive, setDragActive] = useState(false)
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const validateFiles = (newFiles: File[]): string | null => {
    for (const file of newFiles) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        return `Invalid file type. Only PDF, JPG, PNG allowed.`
      }
      if (file.size > MAX_FILE_SIZE) {
        return `File too large. Max 5MB per file.`
      }
    }
    if (files.length + newFiles.length > MAX_FILES) {
      return `Max ${MAX_FILES} files allowed.`
    }
    return null
  }

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const droppedFiles = Array.from(e.dataTransfer.files)
    const validationError = validateFiles(droppedFiles)
    if (validationError) {
      setError(validationError)
      return
    }

    const updatedFiles = [...files, ...droppedFiles]
    onFilesChange(updatedFiles)
    setError('')
  }, [files, onFilesChange])

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(true)
  }, [])

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
  }, [])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    const validationError = validateFiles(selectedFiles)
    if (validationError) {
      setError(validationError)
      return
    }

    const updatedFiles = [...files, ...selectedFiles]
    onFilesChange(updatedFiles)
    setError('')
    // Reset input
    e.target.value = ''
  }, [files, onFilesChange])

  const removeFile = useCallback((index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index)
    onFilesChange(updatedFiles)
  }, [files, onFilesChange])

  const getFileIcon = (file: File) => {
    if (file.type === 'application/pdf') return FileText
    if (file.type.startsWith('image/')) return Image
    return File
  }

  const isValidFileCount = files.length >= (required ? 1 : 0)

  return (
   <Card className={`border rounded-xl shadow-sm ${className}`}>
  <CardContent className="p-4 space-y-4">

    {/* Upload Area */}
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={() => fileInputRef.current?.click()}
      className={`
        border-2 border-dashed rounded-lg p-4 cursor-pointer transition-all
        flex items-center justify-between gap-4
        hover:border-primary hover:bg-accent/40
        ${dragActive ? 'border-primary bg-primary/5 scale-[1.01]' : ''}
        ${files.length >= MAX_FILES ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={files.length >= MAX_FILES}
      />

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Upload className="h-5 w-5 text-primary" />
        </div>

        <div>
          <p className="text-sm font-semibold">
            {dragActive ? 'Drop files here' : 'Upload documents'}
          </p>
          <p className="text-xs text-muted-foreground">
            PDF, JPG, PNG • Max 5MB
          </p>
        </div>
      </div>

      <Badge variant="secondary" className="text-xs">
        {files.length}/{MAX_FILES}
      </Badge>
    </div>

    {/* Error */}
    {error && (
      <div className="text-xs px-3 py-2 rounded-md bg-destructive/10 text-destructive border border-destructive/20">
        {error}
      </div>
    )}

    {/* Files */}
    {files.length > 0 && (
      <div className="space-y-2">
        {files.map((file, index) => {
          const Icon = getFileIcon(file)
          const previewUrl = file.type.startsWith('image/')
            ? URL.createObjectURL(file)
            : undefined

          return (
            <div
              key={index}
              className="flex items-center gap-3 p-2 rounded-lg border bg-background hover:bg-accent transition"
            >
              <Avatar className="h-9 w-9">
                <AvatarImage src={previewUrl} />
                <AvatarFallback>
                  <Icon className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {file.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {(file.size / 1024 / 1024).toFixed(1)} MB
                </p>
              </div>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => removeFile(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )
        })}
      </div>
    )}

  </CardContent>
</Card>)
}

